import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import Skeleton from "@/common/components/Skeleton";
import { SearchableModel } from "@/common/types/searchableModel.type";
import { ChevronRightIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";

interface ListFromSelectedModelProps {
  enabled: boolean;
  model: SearchableModel;
  search: string;
  onSelect: (callback: () => void) => void;
  selectedIndex: number;
  handleKeyboard: (
    onArrowDown: (index: number) => number,
    onSelect: (index: number) => void
  ) => (event: KeyboardEvent) => void;
  handleKeyScroll: (listElement: HTMLDivElement, selectedItemElement: HTMLDivElement) => void;
}

const ListFromSelectedModel = ({
  enabled,
  model: { hook, mapper },
  search,
  onSelect,
  selectedIndex,
  handleKeyboard,
  handleKeyScroll,
}: ListFromSelectedModelProps) => {
  const navigate = useNavigate();

  const listRef = useRef<HTMLDivElement>(null);
  const selectedItemRef = useRef<HTMLDivElement>(null);

  const { data, meta, isFetching, refetch, error } = hook({ filters: { search }, mapper: mapper() });

  useEffect(() => {
    if (isFetching) {
      return;
    }

    const onKeyboardArrowDown = (index: number) => {
      if (index < (meta ? meta?.current_page_size : data.length) - 1) {
        return index + 1;
      }

      return index;
    };

    const onKeyboardSelect = (index: number) => {
      const selectedItem = data[index];

      if (selectedItem) {
        onSelect(() => {
          navigate(selectedItem.route);
        });
      }
    };

    const handleKeydown = (event: KeyboardEvent) => handleKeyboard(onKeyboardArrowDown, onKeyboardSelect)(event);

    if (enabled) {
      document.removeEventListener('keydown', handleKeydown, true);
      document.addEventListener('keydown', handleKeydown, true);
    } else {
      document.removeEventListener('keydown', handleKeydown, true);
    }

    return () => document.removeEventListener('keydown', handleKeydown, true);
  }, [enabled, selectedIndex, search, meta?.current_page_size, isFetching]);

  useEffect(() => {
    const listElement = listRef.current;
    const selectedItemElement = selectedItemRef.current;

    if (listElement && selectedItemElement) {
      handleKeyScroll(listElement, selectedItemElement);
    }
  }, [selectedIndex]);

  return (
    <div className="flex flex-col gap-2 overflow-auto h-72" ref={listRef}>
      {!isFetching && (
        <ErrorMessageWrapper
          className="overflow-hidden text-gray-200 -gap-24 -mt-12"
          refetchFunction={refetch}
          statusCode={error?.status}
        >
          {data.length ? data?.map((item, index) => {
            return (
              <div
                className={twMerge(
                  'bg-white-100 flex items-center justify-between p-4 rounded-lg text-white-400 hover:bg-white-200 cursor-pointer',
                  index === selectedIndex && 'border-2 border-white-400 bg-white-200'
                )}
                onClick={() => onSelect(() => navigate(item.route))}
                ref={index === selectedIndex ? selectedItemRef : null}
              >
                <div className="flex items-end gap-5 flex-wrap">
                  {item.items.map(([label, value]: [label: string, value?: string]) => (
                    <div>
                      <p className="text-xs font-light italic">{label}</p>
                      <p>{value || '--'}</p>
                    </div>
                  ))}
                </div>

                <ChevronRightIcon strokeWidth={1.5} />
              </div>
            );
          }) : (
            <EmptyDataMessage className="overflow-hidden text-gray-200 -gap-24 -mt-12" />
          )}
        </ErrorMessageWrapper>
      )}

      {isFetching && (
        new Array(10).fill(0).map(() => (
          <Skeleton className="min-h-[68px]" />
        ))
      )}
    </div>
  );
};

export default ListFromSelectedModel;
